/**
 * Gate for the dev-only mesh download button.
 *
 * The button always renders in `npm run dev`. For deployed builds it renders
 * only when the page's hostname is listed in `VITE_MESH_DOWNLOAD_HOSTS`, a
 * comma-separated allowlist baked in at build time.
 *
 * Accepted entry shapes (case-insensitive, whitespace trimmed):
 *   dev.benx.bengaitlabs.com          exact hostname
 *   https://uat.benx.bengaitlabs.com  full URL (only the hostname is used)
 *   *.bengaitlabs.com                 wildcard: any subdomain, not the apex
 */

/** Normalize one allowlist entry to a bare hostname (or wildcard pattern). */
const normalizeEntry = (entry) => {
  const raw = String(entry ?? '').trim().toLowerCase();
  if (!raw) return '';
  const withoutScheme = raw.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
  const hostOnly = withoutScheme.split('/')[0].split('?')[0].split('#')[0];
  return hostOnly.replace(/:\d+$/, ''); // drop port
};

/** Parse `VITE_MESH_DOWNLOAD_HOSTS` into a list of normalized entries. */
export const parseAllowedHosts = (value) =>
  String(value ?? '')
    .split(',')
    .map(normalizeEntry)
    .filter(Boolean);

/** True when `hostname` matches an allowlist entry (exact or `*.` wildcard). */
export const hostMatchesAllowlist = (hostname, allowed) => {
  const host = normalizeEntry(hostname);
  if (!host) return false;
  return allowed.some((entry) => {
    if (entry.startsWith('*.')) {
      const suffix = entry.slice(1); // ".bengaitlabs.com"
      return host.endsWith(suffix) && host.length > suffix.length;
    }
    return host === entry;
  });
};

/**
 * Whether the mesh download button may render in the current environment.
 * Dev builds: always. Production builds: only allowlisted hostnames.
 */
export const isMeshDownloadAllowed = () => {
  if (import.meta.env.DEV) return true;
  const allowed = parseAllowedHosts(import.meta.env.VITE_MESH_DOWNLOAD_HOSTS);
  if (!allowed.length) return false;
  if (typeof window === 'undefined') return false;
  return hostMatchesAllowlist(window.location.hostname, allowed);
};

export default isMeshDownloadAllowed;
